import { useState } from "react";
import { motion } from "framer-motion";
import { fadeUp, staggerContainer } from "@/lib/animations";
import SectionTitle from "../ui/section-title";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { FiMail } from "react-icons/fi";
import { FaInstagram, FaLinkedin, FaTiktok } from "react-icons/fa";
import { useToast } from "@/hooks/use-toast";

import { ContactRequest, useContactRequest } from "@/services/api_client";

interface FormData {
  name: string;
  email: string;
  company: string;
  message: string;
}

export default function ContactSection() {
  const { createContactRequest } = useContactRequest();

  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    company: "",
    message: "",
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const contactRequest: ContactRequest = {
        name: formData.name,
        email: formData.email,
        company: formData.company,
        message: formData.message,
      };

      await createContactRequest(contactRequest)
      
      toast({
        title: "Message sent!",
        description: "We'll get back to you as soon as possible.",
      });
      
      // Reset form
      setFormData({
        name: "",
        email: "",
        company: "",
        message: "",
      });
    } catch (error) {
      console.error("Error sending message:", error);
      toast({
        title: "Error sending message",
        description: "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <section id="contact" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <SectionTitle 
          title="Ready to Elevate Your Content?" 
          subtitle="Let's talk about how AI-driven content can amplify your brand's reach and results." 
        />
        
        <div className="max-w-2xl mx-auto">
          <motion.form 
            onSubmit={handleSubmit}
            className="grid grid-cols-1 md:grid-cols-2 gap-6"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
          >
            <motion.div className="md:col-span-2" variants={fadeUp}>
              <Label htmlFor="name">Full Name</Label>
              <Input 
                id="name" 
                name="name" 
                value={formData.name}
                onChange={handleChange}
                required 
                className="mt-1" 
              />
            </motion.div>
            
            <motion.div variants={fadeUp}>
              <Label htmlFor="email">Email</Label>
              <Input 
                id="email" 
                name="email" 
                type="email"
                value={formData.email}
                onChange={handleChange} 
                required 
                className="mt-1" 
              />
            </motion.div>
            
            <motion.div variants={fadeUp}>
              <Label htmlFor="company">Company</Label>
              <Input 
                id="company" 
                name="company" 
                value={formData.company}
                onChange={handleChange}
                className="mt-1" 
              />
            </motion.div>
            
            <motion.div className="md:col-span-2" variants={fadeUp}>
              <Label htmlFor="message">Message</Label>
              <Textarea 
                id="message" 
                name="message" 
                rows={4} 
                value={formData.message}
                onChange={handleChange}
                required 
                className="mt-1" 
              />
            </motion.div>
            
            <motion.div className="md:col-span-2 text-center" variants={fadeUp}>
              <Button 
                type="submit" 
                className="inline-flex items-center px-8 py-4 h-auto bg-purple-600 text-white font-medium rounded-full shadow-lg hover:bg-purple-700 transition-all"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Sending..." : "Send Message"}
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className="h-5 w-5 ml-2" 
                  viewBox="0 0 20 20" 
                  fill="currentColor"
                >
                  <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
                </svg>
              </Button>
            </motion.div>
          </motion.form>
          
          <motion.div 
            className="flex justify-center space-x-6 mt-12"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            custom={3}
          >
            <motion.a 
              href="mailto:info@grapegraph.ai" 
              className="text-purple-600 hover:text-purple-800 transition-colors"
              aria-label="Email us"
              whileHover={{ scale: 1.1 }}
            >
              <FiMail className="h-8 w-8" />
            </motion.a>
            
            <motion.a 
              href="https://www.instagram.com/grapegraphai" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-purple-600 hover:text-purple-800 transition-colors"
              aria-label="Follow us on Instagram"
              whileHover={{ scale: 1.1 }}
            >
              <FaInstagram className="h-8 w-8" />
            </motion.a>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
